/**
 * Streaming GFM table renderer with per-row memoization.
 *
 * react-markdown treats a whole table as one block, so growing the markdown
 * string rebuilds every `<tr>`. This component parses the table once via
 * `marked`, keys rows by append-only index, and memoizes each row on cell
 * content so completed rows keep their DOM nodes. New rows mount with a short
 * fade-in; existing rows do not re-animate.
 */
import { Box, keyframes } from "@mui/material";
import { marked, type Tokens } from "marked";
import { Children, isValidElement, memo, useEffect, useMemo, useRef, type ReactNode } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

const WIDE_TABLE_COLUMN_THRESHOLD = 5;
const WIDE_TABLE_CELL_MIN_WIDTH = "9rem";

const rowFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const tableScrollSx = {
  mt: 2.5,
  mb: 3.25,
  borderRadius: 3,
  maxWidth: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarGutter: "stable",
  WebkitOverflowScrolling: "touch",
} as const;

const narrowTableScrollSx = {
  ...tableScrollSx,
  "--scout-table-cell-min-width": "min-content",
} as const;

const wideTableScrollSx = {
  ...tableScrollSx,
  "--scout-table-cell-min-width": WIDE_TABLE_CELL_MIN_WIDTH,
} as const;

const tableSx = {
  borderCollapse: "separate",
  borderSpacing: 0,
  width: "100%",
  minWidth: "min-content",
  tableLayout: "auto",
  fontSize: "0.9375rem",
  "& tbody tr:last-of-type td": {
    borderBottom: 0,
  },
  "& thead tr:first-of-type th:first-of-type": {
    borderTopLeftRadius: 3,
  },
  "& thead tr:first-of-type th:last-of-type": {
    borderTopRightRadius: 3,
  },
  "& tbody tr:last-of-type td:first-of-type": {
    borderBottomLeftRadius: 3,
  },
  "& tbody tr:last-of-type td:last-of-type": {
    borderBottomRightRadius: 3,
  },
} as const;

const thSx = {
  borderBottom: 1,
  borderColor: "grey.500",
  color: "text.primary",
  fontSize: "0.9375rem",
  fontWeight: 700,
  letterSpacing: "0.02em",
  minWidth: "var(--scout-table-cell-min-width)",
  overflowWrap: "normal",
  wordBreak: "normal",
  px: 1.75,
  py: 0.75,
  textAlign: "left",
} as const;

const tdSx = {
  borderBottom: 1,
  borderColor: "divider",
  minWidth: "var(--scout-table-cell-min-width)",
  overflowWrap: "normal",
  wordBreak: "normal",
  px: 1.75,
  py: 0.75,
  verticalAlign: "top",
} as const;

function countTableColumns(children?: ReactNode): number {
  let columnCount = 0;
  const visit = (node?: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement<{ children?: ReactNode }>(child)) return;
      if (child.type === "tr") {
        let cellsInRow = 0;
        Children.forEach(child.props.children, (cell) => {
          if (isValidElement(cell)) cellsInRow += 1;
        });
        columnCount = Math.max(columnCount, cellsInRow);
        return;
      }
      visit(child.props.children);
    });
  };
  visit(children);
  return columnCount;
}

/**
 * Styled table chrome for tables that still go through react-markdown
 * (e.g. a table nested in a list or blockquote). Top-level GFM table blocks
 * use {@link StreamingMarkdownTable} instead.
 */
export function NestedMarkdownTable({ children }: { children?: ReactNode }) {
  const isWide = countTableColumns(children) >= WIDE_TABLE_COLUMN_THRESHOLD;
  return (
    <Box sx={isWide ? wideTableScrollSx : narrowTableScrollSx}>
      <Box component="table" sx={tableSx}>
        {children}
      </Box>
    </Box>
  );
}

export function NestedMarkdownTableHeaderCell({ children }: { children?: ReactNode }) {
  return (
    <Box component="th" sx={thSx}>
      {children}
    </Box>
  );
}

export function NestedMarkdownTableDataCell({ children }: { children?: ReactNode }) {
  return (
    <Box component="td" sx={tdSx}>
      {children}
    </Box>
  );
}

const rowSx = {
  animation: `${rowFadeIn} 160ms ease-out`,
} as const;

const cellRemarkPlugins = [remarkGfm, remarkBreaks];

export type ParsedGfmTable = {
  header: string[];
  rows: string[][];
};

function cellRawMarkdown(cell: Tokens.TableCell): string {
  return cell.tokens.map((token) => token.raw).join("") || cell.text;
}

/**
 * True when `content` is a single GFM table block (the shape produced by
 * `parseMarkdownIntoBlocks` after table stabilization).
 */
export function isGfmTableMarkdownBlock(content: string): boolean {
  const tokens = marked.lexer(content).filter((token) => token.raw.trim().length > 0);
  return tokens.length === 1 && tokens[0]?.type === "table";
}

export function parseGfmTableMarkdown(markdown: string): ParsedGfmTable | null {
  const tokens = marked.lexer(markdown);
  const table = tokens.find((token): token is Tokens.Table => token.type === "table");
  if (!table) {
    return null;
  }

  return {
    header: table.header.map(cellRawMarkdown),
    rows: table.rows.map((row) => row.map(cellRawMarkdown)),
  };
}

function cellsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

function TableCellMarkdown({ markdown, components }: { markdown: string; components: Components }) {
  return (
    <Markdown remarkPlugins={cellRemarkPlugins} components={components}>
      {markdown}
    </Markdown>
  );
}

const MemoizedStreamingTableRow = memo(
  function MemoizedStreamingTableRow({
    cells,
    isHeader,
    animateOnMount,
    cellComponents,
  }: {
    cells: string[];
    isHeader: boolean;
    animateOnMount: boolean;
    cellComponents: Components;
  }) {
    // Capture once: flipping animate off after mount would re-render the row and flash.
    const shouldAnimate = useRef(animateOnMount).current;

    return (
      <Box component="tr" sx={shouldAnimate ? rowSx : undefined}>
        {cells.map((cell, index) => (
          <Box key={index} component={isHeader ? "th" : "td"} sx={isHeader ? thSx : tdSx}>
            <TableCellMarkdown markdown={cell} components={cellComponents} />
          </Box>
        ))}
      </Box>
    );
  },
  (prev, next) =>
    prev.isHeader === next.isHeader &&
    prev.cellComponents === next.cellComponents &&
    cellsEqual(prev.cells, next.cells)
);
MemoizedStreamingTableRow.displayName = "MemoizedStreamingTableRow";

/**
 * Cell component map for table cells: unwrap the paragraph react-markdown wraps
 * around inline content so cells stay on one line, and reuse Scout link/chip
 * handling from the parent message renderer.
 */
export function createTableCellMarkdownComponents(linkComponent: Components["a"]): Components {
  return {
    p: ({ children }: { children?: ReactNode }) => <>{children}</>,
    a: linkComponent,
  };
}

type StreamingMarkdownTableProps = {
  markdown: string;
  cellComponents: Components;
};

export const StreamingMarkdownTable = memo(function StreamingMarkdownTable({
  markdown,
  cellComponents,
}: StreamingMarkdownTableProps) {
  const parsed = useMemo(() => parseGfmTableMarkdown(markdown), [markdown]);
  // null on first paint → treat as "already this long" so history / first paint
  // does not fade every row; only rows appended after mount animate in.
  const previousRowCountRef = useRef<number | null>(null);
  const rowCount = parsed?.rows.length ?? 0;
  const fadeFromIndex = previousRowCountRef.current ?? rowCount;

  useEffect(() => {
    previousRowCountRef.current = rowCount;
  }, [rowCount]);

  if (!parsed) {
    return null;
  }

  const columnCount = parsed.header.length;
  const isWide = columnCount >= WIDE_TABLE_COLUMN_THRESHOLD;

  return (
    <Box sx={isWide ? wideTableScrollSx : narrowTableScrollSx}>
      <Box component="table" sx={tableSx}>
        <Box component="thead">
          <MemoizedStreamingTableRow
            cells={parsed.header}
            isHeader
            animateOnMount={false}
            cellComponents={cellComponents}
          />
        </Box>
        <Box component="tbody">
          {parsed.rows.map((row, index) => (
            <MemoizedStreamingTableRow
              // Append-only stream: index is stable for every completed row.
              key={index}
              cells={row}
              isHeader={false}
              animateOnMount={index >= fadeFromIndex}
              cellComponents={cellComponents}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
});
StreamingMarkdownTable.displayName = "StreamingMarkdownTable";
